import React, { useEffect, useRef } from 'react'
import { useEditorStore } from '../store.js'

export function EditorCanvas() {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasAreaRef = useRef<HTMLDivElement | null>(null)
  const selectedElementRef = useRef<Element | null>(null)
  const markup = useEditorStore(state => state.markup)
  const selectedNodeId = useEditorStore(state => state.selectedNodeId)
  const zoom = useEditorStore(state => state.zoom)
  const setSelectedNodeHtml = useEditorStore(state => state.setSelectedNodeHtml)

  useEffect(() => {
    if (!containerRef.current || !markup) return

    console.log('EditorCanvas useEffect triggered, markup length:', markup.length)
    console.log('First 200 chars:', markup.substring(0, 200))

    // Render the Web Components directly
    containerRef.current.innerHTML = markup

    // Get reference to canvas-area parent
    canvasAreaRef.current = containerRef.current.parentElement as HTMLDivElement | null

    // Add click handlers for selection
    const handleClick = (e: Event) => {
      const target = e.target as Element

      // If clicking on the container itself (background) or canvas-area, deselect
      if (target === containerRef.current || target === canvasAreaRef.current) {
        e.stopPropagation()
        e.preventDefault()
        selectedElementRef.current = null
        setSelectedNodeHtml(null)
        useEditorStore.setState({ selectedNodeId: null })
        return
      }

      e.stopPropagation()
      e.preventDefault()

      // Store the clicked element directly
      selectedElementRef.current = target

      // Store the HTML snippet for AI context
      if (target instanceof HTMLElement) {
        setSelectedNodeHtml(target.outerHTML)
      }

      // Trigger a re-render by setting a dummy state value
      useEditorStore.setState({ selectedNodeId: Date.now().toString() })
    }

    const handleCanvasAreaClick = (e: Event) => {
      const target = e.target as Element
      if (target === canvasAreaRef.current) {
        selectedElementRef.current = null
        setSelectedNodeHtml(null)
        useEditorStore.setState({ selectedNodeId: null })
      }
    }

    containerRef.current.addEventListener('click', handleClick)
    canvasAreaRef.current?.addEventListener('click', handleCanvasAreaClick)

    return () => {
      containerRef.current?.removeEventListener('click', handleClick)
      canvasAreaRef.current?.removeEventListener('click', handleCanvasAreaClick)
    }
  }, [markup, setSelectedNodeHtml])

  // Add visual selection overlay
  useEffect(() => {
    if (!containerRef.current) return

    const elements = containerRef.current.querySelectorAll('*')
    elements.forEach(el => {
      if (el instanceof HTMLElement) {
        if (el === selectedElementRef.current) {
          el.style.outline = '2px solid #2563eb'
          el.style.outlineOffset = '2px'
        } else {
          el.style.outline = ''
          el.style.outlineOffset = ''
        }
      }
    })
  }, [selectedNodeId])

  if (!markup) {
    return <div className="editor-canvas__empty">Paste or write WireframeHTML markup to begin.</div>
  }

  return (
    <div
      ref={containerRef}
      id="wireframelm-canvas-root"
      className="editor-canvas"
      style={{
        cursor: 'pointer',
        transform: `scale(${zoom / 100})`,
        transformOrigin: 'top left',
        width: `${100 / (zoom / 100)}%`,
        height: `${100 / (zoom / 100)}%`
      }}
    />
  )
}
